import { useNavigate } from 'react-router-dom';
import React, { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Divider, Dropdown, Form, Input, message, Space, Table, Tooltip, Typography, Switch, InputNumber, Tag, Modal } from 'antd';
import { useHorizontalScrollGuard } from '@/hooks/useHorizontalScrollGuard';
import { useFilterPanelVisible } from '@/hooks/useFilterPanelVisible';
import { useTableScrollY } from '@/hooks/useTableScrollY';
import { useVerticalScrollShadow } from '@/hooks/useVerticalScrollShadow';
import { useEnableTableScrollYLayout } from '@/layouts/tableScrollLayout';
import { workspaceMedProjectModulesPath } from '@/layouts/medProjectWorkspacePaths';
import { projectActions } from '../store/projectSlice';
import { moduleListApi, fetchMedProjects, changeMedProjectStatus, medProjectMarkAsTesting } from '@services/MedProject';
import { convertDateTimeToLocalText, numberProject } from '@utils/utils';
import copy from 'copy-to-clipboard';
import { projectStatusList } from '../constants';
import config from '@config';
import QRCode from 'qrcode.react';
import PageHeader from '@components/PageHeader/PageHeader';
import MedProjectsFilterPanel from './MedProjectsFilterPanel';
import './MedProject.less';
import { TABLE_PAGE_SIZE_OPTIONS } from '@/utils/property';

const { Text } = Typography;

const { setProjectFilter: setFilter, setProjectCursor: setCursor } = projectActions;

const EditableContext = React.createContext(null);
const EditableRow = ({ index, ...props }) => {
    const [form] = Form.useForm();
    return (
        <Form form={form} component={false}>
            <EditableContext.Provider value={form}>
                <tr {...props} />
            </EditableContext.Provider>
        </Form>
    );
};
const EditableCell = ({ title, editable, children, dataIndex, record, handleSave, ...restProps }) => {
    const [editing, setEditing] = useState(false);
    const inputRef = useRef(null);
    const form = useContext(EditableContext);
    useEffect(() => {
        if (editing) {
            inputRef.current.focus();
        }
    }, [editing]);
    const toggleEdit = () => {
        setEditing(!editing);
        form.setFieldsValue({
            [dataIndex]: record[dataIndex],
        });
    };
    const save = async () => {
        try {
            const values = await form.validateFields();
            toggleEdit();
            handleSave({
                ...record,
                ...values,
            });
        } catch (errInfo) {
            console.log('Save failed:', errInfo);
        }
    };
    let childNode = children;
    if (editable) {
        childNode = editing ? (
            <Form.Item
                style={{
                    margin: 0,
                }}
                name={dataIndex}
                rules={[
                    {
                        required: true,
                        message: `排序值不能为空`,
                    },
                ]}
            >
                <InputNumber ref={inputRef} min={0} max={9999} onPressEnter={save} onBlur={save} />
            </Form.Item>
        ) : (
            <div
                className="editable-cell-value-wrap"
                style={{
                    paddingRight: 24,
                }}
                onClick={toggleEdit}
            >
                {children}
            </div>
        );
    }
    return <td {...restProps}>{childNode}</td>;
};

export default function MedProjects() {
    const navigate = useNavigate();

    const dispatch = useDispatch();
    const filter = useSelector(state => state.project.project.filter);
    const cursor = useSelector(state => state.project.project.cursor);
    const { filterToggleProps, FilterPanelSlot } = useFilterPanelVisible();

    const tableRegionRef = useRef(null);
    const tableScrollContainerRef = useRef(null);
    const [scrollContainerVersion, setScrollContainerVersion] = useState(0);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const enableTableScrollYLayout = useEnableTableScrollYLayout();
    const tableDeductSelectors = useMemo(() => ['.main-toolbar'], []);
    const tableScrollY = useTableScrollY(tableRegionRef, {
        enabled: enableTableScrollYLayout || isFullscreen,
        heightRootClassName: 'main-container-card',
        scrollYOffset: 0,
        deductSiblingSelectors: tableDeductSelectors,
    });

    useHorizontalScrollGuard(tableScrollContainerRef, {
        threshold: 6,
        edgeHoldMs: 220,
        watchValue: scrollContainerVersion,
    });
    useVerticalScrollShadow(tableScrollContainerRef, {
        threshold: 2,
        watchValue: scrollContainerVersion,
    });

    useLayoutEffect(() => {
        const region = tableRegionRef.current;
        if (!region) return;

        const resolveScrollContainer = () => {
            const next = region.querySelector('.ant-table-body') ?? region.querySelector('.ant-table-content');
            if (tableScrollContainerRef.current !== next) {
                tableScrollContainerRef.current = next;
                setScrollContainerVersion(v => v + 1);
            }
        };

        resolveScrollContainer();

        let rafId = 0;
        const mutationObserver = new MutationObserver(() => {
            if (rafId) cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(() => {
                rafId = 0;
                resolveScrollContainer();
            });
        });
        mutationObserver.observe(region, { childList: true, subtree: true });

        return () => {
            if (rafId) cancelAnimationFrame(rafId);
            mutationObserver.disconnect();
        };
    }, []);

    const handleToggleFullscreen = useCallback(() => {
        const target = document.querySelector('main.main-container');
        if (!target) return;
        const next = !target.classList.contains('is-page-fullscreen');
        target.classList.toggle('is-page-fullscreen', next);
        setIsFullscreen(next);
    }, []);

    useEffect(() => {
        const onKeyDown = e => {
            if (e.key !== 'Escape') return;
            const target = document.querySelector('main.main-container.is-page-fullscreen');
            if (!target) return;
            target.classList.remove('is-page-fullscreen');
            setIsFullscreen(false);
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, []);

    useEffect(() => {
        return () => {
            const target = document.querySelector('main.main-container.is-page-fullscreen');
            if (target) target.classList.remove('is-page-fullscreen');
        };
    }, []);

    const [tableData, setTableData] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);

    const [shareCodeOpen, setShareCodeOpen] = useState(false);
    const [shareForm] = Form.useForm();
    const [wxacode, setWxacode] = useState('');

    useEffect(() => {
        document.title = '医项目管理-医项目';
    }, []);

    const getProjects = async () => {
        const params = { ...filter };

        try {
            setLoading(true);

            const res = await fetchMedProjects(params);
            if (res.status_code === 200) {
                setTotal(res.data.totalCount);
                setTableData(res.data.list);
            }

            setLoading(false);
        } catch (_e) {
            setLoading(false);
        }
    };

    const onFinish = values => {
        let idString = values.id;
        if (!!idString && /^\s*[a-z]+\d+\s*$/i.test(idString)) {
            idString = idString.replace(/^\s*[a-z]+0+([1-9]\d*)\s*$/i, '$1');
        }

        dispatch(
            setFilter({
                ...filter,
                id: idString,
                name: values.name,
                status: values.status,
                hidden: values.hidden,
                createdBy: values.createdBy,
                isTesting: values.isTesting,
                page: 1,
            }),
        );
    };

    const onReset = () => {
        dispatch(
            setFilter({
                ...filter,
                id: undefined,
                name: undefined,
                status: undefined,
                hidden: undefined,
                createdBy: undefined,
                isTesting: 0,
                page: 1,
            }),
        );
    };

    const onChangePage = (page, pageSize) => {
        dispatch(
            setFilter({
                ...filter,
                page,
                pageSize,
            }),
        );
    };

    const onShowSizeChange = (_page, pageSize) => onChangePage(1, pageSize);

    const onRefresh = () => getProjects();

    const copyProjectNo = projectNo => {
        copy(projectNo);
        message.success('已复制到剪贴板');
    };

    const onChangeProjectStatus = async (projectId, status) => {
        const res = await changeMedProjectStatus({
            id: projectId,
            status,
        });
        if (res.status_code === 200) {
            if (status === 1) {
                message.success('项目上架成功');
            } else if (status === 2) {
                message.success('项目下架成功');
            }

            getProjects();

            dispatch(setCursor(projectId));
        }
    };

    const toCreate = () => navigate('/medproject/projects/new');

    // 管理项目（跳转至模块管理）
    const toProjects = async id => {
        const res = await moduleListApi({ projectId: id });
        if (res.status_code === 200) {
            if (res.data?.length) {
                const moduleId = res.data.find(m => m.moduleType === 'activity')?.id || 0;
                if (!!moduleId) {
                    navigate(workspaceMedProjectModulesPath(id, moduleId));
                } else {
                    navigate(`/medproject/projects/${id}/info`);
                }
            } else {
                navigate(`/medproject/projects/${id}/info`);
            }

            dispatch(setCursor(id));
        }
    };

    // 医项目显示、隐藏
    const handleChangeShow = async (bool, projectId) => {
        const res = await changeMedProjectStatus({
            id: projectId,
            hidden: bool ? 0 : 1,
        });
        if (res.status_code === 200) {
            if (bool) {
                message.success('显示项目成功');
            } else {
                message.success('隐藏项目成功');
            }

            getProjects();

            dispatch(setCursor(projectId));
        }
    };

    const confirmShelfOn = record => {
        Modal.confirm({
            title: '确认上架',
            icon: <i className="bi bi-exclamation-circle"></i>,
            content: '上架后，web/h5正常访问使用医项目链接',
            okText: '确认上架',
            cancelText: '取消',
            centered: true,
            onOk: () => onChangeProjectStatus(record.id, 1),
        });
    };

    const confirmShelfOff = record => {
        Modal.confirm({
            title: '确认下架',
            icon: <i className="bi bi-exclamation-circle"></i>,
            content: (
                <>
                    下架后，若在医项目列表显示将医会在web健康界平台-医项目板块取消展示；<Text type="danger">web/h5无法正常访问使用医项目链接，页面为空并提示已下架</Text>，请您慎重操作
                </>
            ),
            okText: '确认下架',
            cancelText: '取消',
            okButtonProps: { danger: true },
            centered: true,
            onOk: () => onChangeProjectStatus(record.id, 2),
        });
    };

    // 正式/测试项目
    const markAsTesting = record => {
        Modal.confirm({
            title: !!record.isTesting ? '标记为正式项目' : '标记为测试项目',
            icon: <i className="bi bi-exclamation-circle"></i>,
            content: !!record.isTesting ? '确定标记为正式项目吗？' : '确定标记为测试项目吗？',
            okText: '确认',
            cancelText: '取消',
            centered: true,
            onOk: () => {
                medProjectMarkAsTesting({
                    id: record.id,
                    isTesting: !!record.isTesting ? 0 : 1,
                }).then(res => {
                    if (res.status_code === 200) {
                        const msg = !!record.isTesting ? '标记为正式项目成功' : '标记为测试项目成功';
                        message.success(msg);
                        getProjects();
                    }
                });
            },
        });
    };

    const defaultColumns = [
        {
            title: '项目编号',
            dataIndex: 'id',
            width: 80,
            render: (_, record) => {
                const projectNo = numberProject('YXM', record.id);
                return (
                    <span className="conferenctlist-copy-column-span">
                        {projectNo}
                        <span onClick={() => copyProjectNo(projectNo)}>
                            <i className="bi bi-copy"></i>
                        </span>
                    </span>
                );
            },
        },
        {
            title: '项目名称',
            dataIndex: 'name',
            width: 200,
            render: (_, record) => (
                <span className="conferenctlist-copy-column-span">
                    {record.name}
                    {!!record.isTesting && (
                        <Tag color="#f50" style={{ marginLeft: 4, marginRight: 0, padding: 2, fontSize: 10, lineHeight: '1em', verticalAlign: 'middle', display: 'inline' }}>
                            TEST
                        </Tag>
                    )}
                </span>
            ),
        },
        {
            title: '待审核人数',
            dataIndex: 'waitAuditCount',
            width: 100,
            render: (_, record) => <>{record.waitAuditCount > 0 ? <Text type="warning">{record.waitAuditCount}</Text> : <>0</>}</>,
        },
        {
            title: '加入人数',
            dataIndex: 'joinCount',
            width: 80,
            render: (_, record) => record.joinCount || 0,
        },
        {
            title: '项目状态',
            dataIndex: 'status',
            width: 80,
            render: (_, record) => <Text type={projectStatusList.find(s => s.status === record.status)?.type || null}>{projectStatusList.find(s => s.status === record.status)?.label}</Text>,
        },
        {
            title: '创建人',
            dataIndex: 'createdName',
            width: 120,
            render: (_, record) => (
                <>
                    <span>{record?.createdName}</span>
                    <br />
                    <Text type="secondary">
                        <small>{convertDateTimeToLocalText(record?.createdAt)}</small>
                    </Text>
                </>
            ),
        },
        {
            title: (
                <>
                    <span>医项目列表显示</span>
                    <Tooltip title="显示后，医项目将会在web健康界平台-医项目板块展示；显示/隐藏，不影响web/h5正常访问使用医项目链接" overlayStyle={{ maxWidth: '300px' }}>
                        <i className="bi bi-question-circle-fill popover-tip-icon"></i>
                    </Tooltip>
                </>
            ),
            dataIndex: 'rankState',
            width: 120,
            render: (_, row) => {
                if (row.status === 1) {
                    return <Switch checkedChildren="显示" unCheckedChildren="隐藏" checked={!row.hidden} onChange={bool => handleChangeShow(bool, row.id)} />;
                }
            },
        },
        {
            title: (
                <>
                    <span>排序值</span>
                    <Tooltip title="数值越大越在最上方，允许相同数值" overlayStyle={{ maxWidth: '300px' }}>
                        <i className="bi bi-question-circle-fill popover-tip-icon"></i>
                    </Tooltip>
                </>
            ),
            dataIndex: 'sort',
            width: 80,
            editable: true,
            render: (_, row) => {
                if (row.status === 1) {
                    return (
                        <>
                            {row.sort}
                            <i className="bi bi-pencil"></i>
                        </>
                    );
                }
            },
        },
        {
            title: '操作',
            dataIndex: 'operations',
            width: 160,
            fixed: 'right',
            render: (_, record) => {
                const items = [
                    ...(record.status === 2
                        ? [
                              {
                                  label: '上架',
                                  key: 'shelfOn',
                                  onClick: () => confirmShelfOn(record),
                              },
                          ]
                        : []),
                    ...(record.status === 1
                        ? [
                              {
                                  label: '下架',
                                  key: 'shelfOff',
                                  danger: true,
                                  onClick: () => confirmShelfOff(record),
                              },
                          ]
                        : []),
                    {
                        label: !!record.isTesting ? '标为正式项目' : '标为测试项目',
                        key: 'markAsTesting',
                        onClick: () => markAsTesting(record),
                    },
                ];

                return (
                    <Space className="operation-btns" separator={<Divider orientation="vertical" />}>
                        <Button size="small" type="link" onClick={() => toProjects(record.id)}>
                            管理
                        </Button>
                        <Button size="small" type="link" onClick={() => window.open(`${config.PROJECT_DOMAIN}/p/${record.id}`)}>
                            预览
                        </Button>
                        <Button size="small" type="link" onClick={() => handleShare(record)}>
                            分享
                        </Button>
                        <Dropdown menu={{ items }} placement="bottomRight">
                            <Button type="link" size="small" className="more-operation">
                                <i className="bi bi-three-dots"></i>
                            </Button>
                        </Dropdown>
                    </Space>
                );
            },
        },
    ];

    const handleSave = async row => {
        const res = await changeMedProjectStatus({
            id: row.id,
            sort: row.sort,
        });
        if (res.status_code === 200) {
            message.success('保存成功');
            getProjects();
            dispatch(setCursor(row.id));
        }
    };

    // 分享
    const handleShare = record => {
        const link = `${config.PROJECT_DOMAIN}/m/p/${record.id}`;
        shareForm.setFieldValue('h5CodeLink', link);

        const wxLink = `pages/webPage1/webPage1?url=${encodeURIComponent(link)}`;
        shareForm.setFieldValue('wxQrCode', wxLink);

        shareForm.setFieldValue('projectName', record.name);
        getWxCodeImage(wxLink);
        setShareCodeOpen(true);
    };

    // 获取小程序二维码图片
    const getWxCodeImage = async link => {
        const params = {
            env_version: window.location.origin.includes('stage-') || window.location.origin.includes('test-') ? 'trial' : 'release',
            path: link,
            width: 300,
        };
        const url = `${config.API_PREFIX}/dataengine/api/mp/getwxacode`;
        const response = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json;charset=utf-8' },
            body: JSON.stringify(params),
        });

        if (response.ok) {
            const text = await response.text();
            setWxacode(text);
        }
    };

    // 下载二维码
    const downloadQRCode = type => {
        if (type === 'h5') {
            const qrCode = document.getElementById('h5_share');
            const image = new Image();
            image.src = qrCode.toDataURL('image/png');
            const link = document.getElementById(`link_h5_share`);
            link.href = image.src;
        }
        if (type === 'wx') {
            const img = document.getElementById('wx_share');
            // 创建一个 <canvas> 元素
            const canvas = document.createElement('canvas');
            // 设置 <canvas> 的宽度和高度为图片原始的宽度和高度
            canvas.width = img.naturalWidth || img.width;
            canvas.height = img.naturalHeight || img.height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            // 将 <canvas> 内容转换为 Base64 图片数据
            const imageData = canvas.toDataURL('image/png');
            const a = document.createElement('a');
            a.download = `${shareForm.getFieldValue('projectName')}（医项目小程序码）.png`;
            a.href = imageData;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
        }
    };

    // 复制
    const onCopy = () => {
        const h5CodeLink = shareForm.getFieldValue('h5CodeLink');
        copy(h5CodeLink);
        message.success('复制成功');
    };

    const components = {
        body: {
            row: EditableRow,
            cell: EditableCell,
        },
    };

    const columns = defaultColumns.map(col => {
        if (!col.editable) {
            return col;
        }
        return {
            ...col,
            onCell: record => ({
                record,
                editable: col.editable,
                dataIndex: col.dataIndex,
                title: col.title,
                handleSave,
            }),
        };
    });

    // 仅在 Redux filter 变化时请求列表（含分页）；勿将 getProjects 列入依赖以免多余请求
    // biome-ignore lint/correctness/useExhaustiveDependencies: 依赖 filter 语义即可
    useEffect(() => {
        getProjects();
    }, [filter]);

    return (
        <div className="page-container list-page-container medproject-list-page">
            <PageHeader title="医项目管理" breadcrumb={[{ label: '医项目', path: '/medproject/projects' }, { label: '医项目管理' }]} {...filterToggleProps} />

            <FilterPanelSlot>
                <MedProjectsFilterPanel filter={filter} onFinish={onFinish} onReset={onReset} />
            </FilterPanelSlot>

            <div ref={tableRegionRef} className="table-region-container medproject-list-table-region">
                <div className="main-toolbar medproject-list-table-toolbar medproject-list-toolbar">
                    <div className="main-tools">
                        <Button type="primary" onClick={() => toCreate()}>
                            新增医项目
                        </Button>
                    </div>
                    <div className="sub-tools">
                        <Space>
                            <Tooltip title="刷新列表">
                                <Button type="default" onClick={onRefresh} icon={<span className="bi bi-arrow-clockwise" aria-hidden="true" />} aria-label="刷新列表" />
                            </Tooltip>
                            <Tooltip title={isFullscreen ? '退出全屏' : '全屏'}>
                                <Button type="default" onClick={handleToggleFullscreen} icon={<span className={isFullscreen ? 'bi bi-fullscreen-exit' : 'bi bi-fullscreen'} aria-hidden="true" />} aria-label={isFullscreen ? '退出全屏' : '全屏'} />
                            </Tooltip>
                        </Space>
                    </div>
                </div>

                <div className="list-table-wrap medproject-list medproject-list-table-wrap">
                    <Table
                        className={tableScrollY != null ? 'custom-table-scrollbar-always' : undefined}
                        rowKey="id"
                        columns={columns}
                        dataSource={tableData}
                        loading={loading}
                        rowClassName={row => (row.id === cursor ? 'store-cursor' : '')}
                        bordered
                        sticky
                        size="medium"
                        components={components}
                        scroll={{
                            x: 'max-content',
                            ...(tableScrollY != null ? { y: tableScrollY } : {}),
                        }}
                        pagination={{
                            total,
                            showTotal: () => `共${total}条`,
                            current: filter.page,
                            pageSize: filter.pageSize,
                            pageSizeOptions: TABLE_PAGE_SIZE_OPTIONS,
                            showQuickJumper: true,
                            showSizeChanger: { placement: 'topRight' },
                            onChange: onChangePage,
                            onShowSizeChange,
                            style: { marginBottom: 0 },
                        }}
                    />
                </div>
            </div>

            <Modal title="医项目分享" open={shareCodeOpen} destroyOnHidden={true} centered width={600} mask={{ closable: false }} onCancel={() => setShareCodeOpen(false)} footer={null}>
                <Form form={shareForm} labelCol={{ span: 4 }} wrapperCol={{ span: 20 }}>
                    <Form.Item name="wxQrCode" label="小程序分享" className="sign-wp-setting-form" help="用户扫码进入小程序-医项目">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div className="mini_program_wrap">
                                <img id="wx_share" src={wxacode} width={150}></img>
                                {wxacode && (
                                    <Button onClick={() => downloadQRCode('wx')} style={{ marginLeft: 16 }}>
                                        下载小程序码
                                    </Button>
                                )}
                            </div>
                        </div>
                    </Form.Item>

                    <Form.Item name="qrcode" label="H5 分享" className="sign-wp-setting-form" help="用户扫码/点击链接进入H5-医项目">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div className="mini_program_wrap" style={{ display: 'flex', alignItems: 'center' }}>
                                <QRCode id="h5_share" size={150} value={shareForm.getFieldValue('h5CodeLink')} />

                                <Button style={{ marginLeft: 16 }}>
                                    <a download={`${shareForm.getFieldValue('projectName')}（医项目h5二维码）`} id={`link_h5_share`} onClick={() => downloadQRCode('h5')}>
                                        下载二维码
                                    </a>
                                </Button>
                            </div>
                        </div>
                    </Form.Item>

                    <Form.Item name="h5CodeLink" label="H5 链接" className="sign-wp-setting-form">
                        <Input.Search placeholder="input search text" allowClear enterButton="复制" size="large" readOnly={true} onSearch={onCopy} />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
}
